import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'
import { Bar } from 'react-chartjs-2'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { v4 as uuidv4 } from 'uuid'
import { useSelector } from 'react-redux'
import { insertIf } from '../../../../../helpers/common'

import GraphContainer from '../GraphContainer'

import { getOptions } from './options'
import { getCustomDataLabelsOptions } from '../reusableGraphOptions'
import { getHtmlLegendPlugin } from '../../../plugins'

import { customBoxSpansTemplate, customContainerStyles } from './customBoxSpan'

import { lineGraphData } from './data'

import { selectedPartnershipCurrency } from '../../../../../modules/selectors/partnerships'
import { socialReturnOnInvestmentReportSelector } from '../../../../../modules/selectors/socialReturnOnInvestment'
import useStyles from './styles'

const HeadcountAndPerBeneficiaryImpactGraph = () => {
  const { t } = useTranslation()
  const classes = useStyles()

  const { currency_symbol: currencySymbol } = useSelector(selectedPartnershipCurrency)
  const { stakeholderImpact } = useSelector(socialReturnOnInvestmentReportSelector)

  const impactGraphData = useMemo(() => {
    return [
      ...insertIf(stakeholderImpact?.Players, stakeholderImpact?.Players?.stakeholderCount),
      ...insertIf(stakeholderImpact?.Attendees, stakeholderImpact?.Attendees?.stakeholderCount),
      ...insertIf(stakeholderImpact?.Viewers, stakeholderImpact?.Viewers?.stakeholderCount)
    ]
  }, [stakeholderImpact])

  const impactGraphDataMaxValue = useMemo(() => Math.max(...impactGraphData), [impactGraphData])

  const impactGraphLabels = useMemo(() => {
    return [
      ...insertIf(stakeholderImpact?.Players, 'Players'),
      ...insertIf(stakeholderImpact?.Attendees, 'Attendees'),
      ...insertIf(stakeholderImpact?.Viewers, 'Fans')
    ]
  }, [stakeholderImpact])

  const impactGraphDatasets = useMemo(
    () => [
      {
        type: 'bar',
        label: 'Headcount',
        data: impactGraphData,
        backgroundColor: '#129686',
        borderRadius: {
          topLeft: 0,
          topRight: 4,
          bottomLeft: 0,
          bottomRight: 4
        },

        borderSkipped: false,
        maxBarThickness: 45,
        datalabels: getCustomDataLabelsOptions({ maxValue: impactGraphDataMaxValue })
      },
      {
        type: 'line',
        label: 'Per Beneficiary Impact',
        data: lineGraphData,

        backgroundColor: '#DB8E46',
        borderColor: '#DB8E46',
        borderWidth: 4,
        fill: false,
        datalabels: {
          color: 'white',
          backgroundColor: '#DB8E46',
          borderColor: 'white',
          borderWidth: 3,
          borderRadius: 4,
          padding: {
            top: 2,
            right: 4,
            bottom: 2,
            left: 4
          },
          align: 'right',
          anchor: 'start',
          font: {
            size: 14
          }
        }
      }
    ],
    [impactGraphData, impactGraphDataMaxValue]
  )

  const graphData = useMemo(
    () => ({
      labels: impactGraphLabels,
      datasets: impactGraphDatasets
    }),
    [impactGraphDatasets, impactGraphLabels]
  )

  const legendContainerId = useMemo(() => uuidv4(), [])

  const options = useMemo(() => getOptions(legendContainerId, currencySymbol), [legendContainerId, currencySymbol])

  const plugins = useMemo(() => {
    const htmlLegendPlugin = getHtmlLegendPlugin(customContainerStyles, customBoxSpansTemplate)

    return [htmlLegendPlugin, ChartDataLabels]
  }, [])

  return (
    <GraphContainer title={t('Headcount and Per Beneficiary Impact')}>
      <Bar type="bar" data={graphData} options={options} plugins={plugins} />
      <div className={classes.legendContainer} id={legendContainerId}>
        {/*// The legend of the graph will be rendered here by the plugin*/}
      </div>
    </GraphContainer>
  )
}

export default HeadcountAndPerBeneficiaryImpactGraph
